// @flow
import React, { useState, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';

import Hidden from '@mui/material/Hidden';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import baseTableStyles from '../Styles/BaseTableStyles.Style';
import { generateCellStyles, generateActionCellStyles } from '../Styles/TableConfigurations';

type TableConfig = {
  columns: Array<any>,
  actions: Array<any>,
  defaultSort?: string,
  enableMultiSelect?: boolean
};

type Props = {
  data: Array<any>,
  config: TableConfig,
  onSort?: (column: string, direction: number) => void,
  sortInfo?: { [string]: number },
  renderCell: (item: any, column: any, index: number) => React.Node,
  renderMobileItem: (item: any, index: number) => React.Node,
  actionHandlers: { [string]: Function },
  className?: string,
  emptyMessage?: string,
  // Multi-select support
  selectedRowsCount?: number,
  handleSelectAllClick?: Function,
  isItemSelected?: Function,
  totalCount?: number
};

const ConfigurableTable = ({
  data,
  config,
  onSort,
  sortInfo,
  renderCell,
  renderMobileItem,
  actionHandlers,
  className = '',
  emptyMessage = 'No items found.',
  selectedRowsCount = 0,
  handleSelectAllClick,
  isItemSelected,
  totalCount = 0
}: Props) => {
  const theme = useTheme();
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);

  const handleSort = useCallback((column) => {
    if (!column.sortable || !onSort) return;
    
    const currentOrder = sortInfo?.[column.key] || 0;
    const newOrder = currentOrder === 1 ? -1 : 1;
    onSort(column.key, newOrder);
  }, [onSort, sortInfo]);

  const handleRowMouseEnter = useCallback((index) => {
    setHoveredRowIndex(index);
  }, []);

  const handleRowMouseLeave = useCallback(() => {
    setHoveredRowIndex(null);
  }, []);

  const handleActionClick = useCallback((actionKey, item, event) => {
    event.stopPropagation();
    const handler = actionHandlers[actionKey];
    if (handler) {
      handler(item);
    }
  }, [actionHandlers]);

  const renderSortIcon = (column) => {
    if (!column.sortable || !sortInfo) return null;
    
    const order = sortInfo[column.key];
    if (order === 1) {
      return <ArrowDownwardIcon sx={baseTableStyles.sortArrow(theme)} />;
    } else if (order === -1) {
      return <ArrowUpwardIcon sx={baseTableStyles.sortArrow(theme)} />;
    }
    return null;
  };

  const renderDesktopActions = (item, index) => {
    if (!config.actions || config.actions.length === 0) return null;

    const isVisible = hoveredRowIndex === index;
    const buttonSize = theme.layout?.listTable?.rowHeight || 42;
    const totalWidth = config.actions.length * buttonSize;

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: totalWidth,
          display: isVisible ? 'flex' : 'none',
          alignItems: 'center',
                     backgroundColor: theme.palette.table?.cellBackground || 'white',
          borderTopRightRadius: theme.shape.borderRadius,
          borderBottomRightRadius: theme.shape.borderRadius,
          zIndex: 10
        }}
      >
        {config.actions.map((action, actionIndex) => {
          const IconComponent = typeof action.icon === 'string' ? 'i' : action.icon;
          const iconProps = typeof action.icon === 'string' 
            ? { className: action.icon }
            : {};

          return (
            <Tooltip
              key={action.key}
              title={action.label}
              placement="top"
            >
              <IconButton
                aria-label={action.label}
                sx={{
                  ...baseTableStyles.configurableActionButton(theme, { mobile: false }),
                  height: '100%',
                  width: buttonSize,
                  minWidth: buttonSize,
                  // Round the right corners of the last button
                  ...(actionIndex === config.actions.length - 1 && {
                    borderTopRightRadius: theme.shape.borderRadius,
                    borderBottomRightRadius: theme.shape.borderRadius
                  })
                }}
                onClick={(e) => handleActionClick(action.onClick, item, e)}
              >
                <IconComponent {...iconProps} />
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  const renderMobileActions = (item) => {
    if (!config.actions || config.actions.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', gap: theme.spacing(0.5) }}>
        {config.actions.map((action) => {
          const IconComponent = typeof action.icon === 'string' ? 'i' : action.icon;
          const iconProps = typeof action.icon === 'string' 
            ? { className: action.icon }
            : {};

          return (
            <IconButton
              key={action.key}
              aria-label={action.label}
              sx={baseTableStyles.mobileActionButton(theme)}
              onClick={(e) => handleActionClick(action.onClick, item, e)}
            >
              <IconComponent {...iconProps} />
            </IconButton>
          );
        })}
      </Box>
    );
  };

  if (!data || data.length === 0) {
    return (
      <Box sx={baseTableStyles.tableContainer(theme)}>
        <Typography variant="body1" sx={{ textAlign: 'center', padding: theme.spacing(4) }}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={baseTableStyles.configurableTableContainer(theme)} className={className}>
      {/* Mobile Version */}
      <Hidden mdUp>
        <Grid container direction="column" sx={{ padding: 0 }}>
          {data.map((item, index) => (
            <Grid
              item
              key={index}
              sx={{
                borderRadius: '12px !important',
                boxShadow: '0px 1px 3px rgba(0,0,0,0.12)',
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(1),
                padding: theme.spacing(1.5),
                backgroundColor: 'white',
                cursor: 'pointer',
                overflow: 'hidden',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                '&.hover': {
                  boxShadow: '0px 2px 8px -1px rgba(0,0,0,0.8)'
                }
              }}
              className={hoveredRowIndex === index ? 'hover' : ''}
              onMouseEnter={() => handleRowMouseEnter(index)}
              onMouseLeave={handleRowMouseLeave}
            >
              {renderMobileItem ? renderMobileItem(item, index) : (
                <Box sx={baseTableStyles.mobileRowContainer(theme)}>
                  <Box>
                    {config.columns
                      .filter(col => !col.hideOnMobile)
                      .map(col => renderCell(item, col, index))}
                  </Box>
                  {renderMobileActions(item)}
                </Box>
              )}
            </Grid>
          ))}
        </Grid>
      </Hidden>

      {/* Desktop Version */}
      <Hidden smDown>
        <Table sx={baseTableStyles.table(theme)}>
          <TableHead sx={baseTableStyles.tableBody(theme)}>
            <TableRow sx={baseTableStyles.tableHeadRow(theme)}>
              {config.columns.map((column, index) => (
                <TableCell
                  key={column.key}
                  sx={generateCellStyles(column, theme, true, index)}
                  onClick={() => handleSort(column)}
                >
                  {column.key === 'checkbox' && config.enableMultiSelect && handleSelectAllClick ? (
                    <Checkbox
                      checked={data.length > 0 && selectedRowsCount === data.length}
                      indeterminate={selectedRowsCount > 0 && selectedRowsCount < data.length}
                      onChange={handleSelectAllClick}
                      color="primary"
                      size="medium"
                    />
                  ) : (
                    <>
                      {column.headerLabel}
                      {renderSortIcon(column)}
                    </>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          
          <TableBody sx={baseTableStyles.tableBody(theme)}>
            {data.map((item, index) => (
              <TableRow
                key={index}
                className={hoveredRowIndex === index ? 'hover' : ''}
                sx={{
                  ...baseTableStyles.tableRow(theme),
                  position: 'relative' // Ensure the row is positioned for absolute children
                }}
                onMouseEnter={() => handleRowMouseEnter(index)}
                onMouseLeave={handleRowMouseLeave}
              >
                {config.columns.map((column, colIndex) => {
                  return (
                    <TableCell
                      key={column.key}
                      sx={generateCellStyles(column, theme, false, colIndex)}
                    >
                      {renderCell(item, column, index)}
                    </TableCell>
                  );
                })}
                {renderDesktopActions(item, index)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Hidden>
    </Box>
  );
};

export default ConfigurableTable; 